/**
 * 匿名身份与令牌（§7.1–§7.3 / §7.12）。
 *
 * 产品前提：**零注册摩擦**。用户点开就能建房，`accountId` 从第一秒就存在且永不变；
 * 以后「升级」只是加一条邮箱密码，不换 id、不迁数据、不打断当前牌局。
 *
 * 三条存放规则（照 §11 的 `auth/store.ts` 分工）：
 *   - `accessToken` **只在内存**：15 分钟就过期，写进 localStorage 只是白白多一个泄漏面；
 *   - `refreshToken` 在 localStorage：刷新页面、切后台被杀，全靠它把账号认回来；
 *   - `deviceId` 在 localStorage：服务端按设备做限流与会话管理，每次换一个会被当成新设备。
 *
 * ★ 单飞（single-flight）是这里的核心。WS 断线重连与 HTTP 请求常常在同一瞬间发现
 *   token 过期，两条路各刷一次就会出现「A 刷出的新 token 把 B 正在用的那条轮换掉」——
 *   服务端的重放检测在 10s 宽限窗外会直接**作废整条 family 并踢掉所有设备**。
 *   所以任何时刻最多只有一个刷新在飞，其余调用方等同一个 Promise。
 *
 * ★ 调用顺序（下游照抄）：
 *     const auth = createAuthSession({ baseUrl })
 *     await auth.ensureAccessToken()       // 没账号就匿名注册，有 refreshToken 就刷新
 *     createWsTransport(wsUrl, () => auth.ensureAccessToken())
 *   WS 的 `tokenProvider` 直接传 `ensureAccessToken`：重连时它会自己判断要不要刷。
 */
import { ulid, type PublicAccount } from '@poker/shared'

export const DEVICE_ID_KEY = 'poker.deviceId.v1'
export const AUTH_KEY = 'poker.auth.v1'

/** access token 剩余不足这么多就提前刷新（§7.10 的「剩余 < 60s 先 refresh」）。 */
export const ACCESS_TOKEN_SKEW_MS = 60_000

export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/** 服务端 `/auth/anonymous` / `/auth/refresh` 的 200 响应体（§7.12）。 */
export interface AuthPairResponse {
  readonly account: PublicAccount
  readonly accessToken: string
  readonly refreshToken: string
  /** 毫秒 TTL。★ 用它而不是 expiresAt：手机本地时钟可能差几分钟 */
  readonly expiresIn: number
  readonly expiresAt?: number
}

/** localStorage 里长期留存的那点东西。★ 绝不含 accessToken。 */
interface PersistedAuth {
  readonly refreshToken: string
  readonly accountId: string
  readonly nickname: string
}

export class AuthError extends Error {
  constructor(
    override readonly message: string,
    readonly code: string,
    readonly status: number,
    /** true = 本地凭据已经没救了，必须重新注册或用邮箱登录 */
    readonly fatal: boolean,
    /**
     * 429 时服务端给的「等多久」。**必须往上传**：
     * 只说「太频繁」而不说等多久，用户只会一秒点一次，把自己锁得更久。
     */
    readonly retryAfterMs: number | null = null,
  ) {
    super(message)
    this.name = 'AuthError'
  }
}

/**
 * 这次通知因何而发。
 *   register：匿名注册（首次打开，或凭据作废后顶了一个新号）
 *   refresh：换票（同一身份，但带回来的账号资料是库里最新的）
 *   adopt：邮箱登录 / 兑换邮件链接 —— 用户主动换人
 *   update：PATCH /me 等接口带回了同一身份的新资料
 *   error / logout：出错、登出
 */
export type AuthChange = 'register' | 'refresh' | 'adopt' | 'update' | 'error' | 'logout'

export interface AuthState {
  readonly account: PublicAccount | null
  readonly deviceId: string
  /** 是否持有可用于刷新的凭据 */
  readonly hasCredential: boolean
  readonly lastError: AuthError | null
  /** 最近一次通知的原因；还没发过通知时为 null */
  readonly change?: AuthChange | null
}

export interface AuthSession {
  deviceId(): string
  account(): PublicAccount | null
  /**
   * 这个会话在跟哪个后端说话（不含 `/api`）。
   * 旁路请求（`auth/emailLinkApi.ts` 等）复用它，免得两处各自拼一遍基址后漂移。
   */
  baseUrl(): string
  /**
   * 外部流程拿到的一对令牌：落盘 refreshToken、把当前身份换成它、通知订阅者。
   *
   * ★ 换身份的**收尾必须走这里**。少了这一步，用户看到「成功」，
   *   手里握着的仍然是原来那个匿名账号 —— 牌桌、筹码一样也回不来。
   */
  adopt(pair: AuthPairResponse): PublicAccount
  /**
   * 同一个身份、只是资料变了（改昵称/改头像走 `PATCH /me`、`POST|DELETE /me/avatar`
   * 之后）。跟 `adopt` 的区别：这里**不换令牌、不落盘、不检查身份代次**——
   * 调用方还是刚才那个账号，只是几个字段更新了，没有「换设备」那一整套顾虑。
   */
  updateAccount(next: PublicAccount): void
  /** 当前内存里的 access token（可能已过期），不触发任何网络请求 */
  peekAccessToken(): string | null
  /**
   * 拿一枚**至少还能用 60 秒**的 access token。
   * 没有账号就匿名注册，有 refreshToken 就刷新；并发调用共用同一个飞行中的请求。
   */
  ensureAccessToken(): Promise<string>
  /** 收到 close(4401) 时用：无视剩余时间强制刷一次（仍然单飞） */
  forceRefresh(): Promise<string>
  /** 显式匿名注册（首次进入或本地凭据作废时） */
  registerAnonymous(nickname?: string): Promise<PublicAccount>
  logout(): Promise<void>
  state(): AuthState
  onChange(cb: (s: AuthState) => void): () => void
}

export interface AuthSessionOptions {
  /** 默认取当前页面同源；dev 下可传 `http://192.168.x.x:8787` 让手机连本机 */
  readonly baseUrl?: string
  readonly fetchImpl?: typeof fetch
  readonly storage?: StorageLike | null
  readonly now?: () => number
  readonly deviceLabel?: string
  readonly skewMs?: number
  /**
   * 后台自动匿名注册（首次打开、凭据作废后顶号）时带上的昵称。不传就由服务端起 `Player-XXXX`。
   * 显式调 `registerAnonymous(nickname)` 时以参数为准。
   */
  readonly nickname?: () => string
}

/** localStorage 在无痕模式 / 关掉站点数据时**读写都会抛**，包一层永不抛的壳。 */
function memoryStorage(): StorageLike {
  const m = new Map<string, string>()
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => {
      m.set(k, v)
    },
    removeItem: (k) => {
      m.delete(k)
    },
  }
}

function safeStorage(provided: StorageLike | null | undefined): StorageLike {
  if (provided != null) return provided
  const ls = (globalThis as { localStorage?: StorageLike }).localStorage
  if (ls == null) return memoryStorage()
  const fallback = memoryStorage()
  return {
    getItem(k) {
      try {
        return ls.getItem(k)
      } catch {
        return fallback.getItem(k)
      }
    },
    setItem(k, v) {
      try {
        ls.setItem(k, v)
      } catch {
        fallback.setItem(k, v)
      }
    },
    removeItem(k) {
      try {
        ls.removeItem(k)
      } catch {
        fallback.removeItem(k)
      }
    },
  }
}

function defaultBaseUrl(): string {
  const loc = (globalThis as { location?: { origin?: string } }).location
  return loc?.origin ?? 'http://localhost:8787'
}

/** 设备标识：形状要满足服务端 `min(8).max(128)` 的校验。 */
function newDeviceId(now: number): string {
  return `dev_${ulid(now)}`
}

function parsePersisted(raw: string | null): PersistedAuth | null {
  if (raw === null) return null
  try {
    const v = JSON.parse(raw) as Partial<PersistedAuth>
    if (typeof v.refreshToken !== 'string' || v.refreshToken.length < 16) return null
    return {
      refreshToken: v.refreshToken,
      accountId: typeof v.accountId === 'string' ? v.accountId : '',
      nickname: typeof v.nickname === 'string' ? v.nickname : '',
    }
  } catch {
    return null
  }
}

interface ErrorBody {
  readonly error?: { readonly code?: string; readonly message?: string; readonly retryAfterMs?: number }
  readonly code?: string
  readonly message?: string
  readonly retryAfterMs?: number
}

/** 本地凭据已经没救的几个码：再刷一百次也是同样的结果，只能重新拿身份。 */
const FATAL_AUTH_CODES = new Set(['E_AUTH_INVALID', 'E_AUTH_EXPIRED', 'E_AUTH_REPLAY', 'E_NOT_AUTHENTICATED'])

/** 响应体解析：服务端出 502 时返回的是一段 HTML，JSON.parse 会抛。 */
export function parseJsonBody(text: string): unknown {
  try {
    return text === '' ? null : JSON.parse(text)
  } catch {
    return null
  }
}

/**
 * 错误体 → `AuthError`。导出是为了让**旁路请求**（邮箱与邮件链接那几个端点）用同一份解析：
 * 错误体的形状（`{error:{code,message,retryAfterMs}}`）只该有一处知道。
 *
 * ★ 带回来的 `message` 是**给开发者看的**。调用方负责翻成人话再上屏，
 *   绝不能把它直接塞进界面（见 `emailLinkApi.ts` 的 `describe`）。
 */
export function readApiError(status: number, parsed: unknown, fallbackMessage = '登录失败'): AuthError {
  const b = (parsed ?? {}) as ErrorBody
  const code = b.error?.code ?? b.code ?? `E_HTTP_${String(status)}`
  const message = b.error?.message ?? b.message ?? fallbackMessage
  const retryAfterMs = b.error?.retryAfterMs ?? b.retryAfterMs ?? null
  return new AuthError(message, code, status, FATAL_AUTH_CODES.has(code), retryAfterMs)
}

export function createAuthSession(options: AuthSessionOptions = {}): AuthSession {
  const baseUrl = (options.baseUrl ?? defaultBaseUrl()).replace(/\/+$/, '')
  const doFetch: typeof fetch = options.fetchImpl ?? ((...args) => globalThis.fetch(...args))
  const store = safeStorage(options.storage)
  const now = options.now ?? (() => Date.now())
  const skewMs = options.skewMs ?? ACCESS_TOKEN_SKEW_MS

  let deviceId = store.getItem(DEVICE_ID_KEY)
  if (deviceId === null || deviceId.length < 8) {
    deviceId = newDeviceId(now())
    store.setItem(DEVICE_ID_KEY, deviceId)
  }

  let persisted = parsePersisted(store.getItem(AUTH_KEY))
  let account: PublicAccount | null = null
  let accessToken: string | null = null
  /** 本地时钟口径的过期时刻 = 收到响应的本地时间 + expiresIn */
  let accessExpiresAtLocal = 0
  let lastError: AuthError | null = null
  let lastChange: AuthChange | null = null
  /** ★ 单飞：同一时刻最多一个「注册或刷新」在飞 */
  let inFlight: Promise<string> | null = null
  /**
   * ★ 身份代次。每次**换人**（邮箱登录、兑换邮件链接、登出）加一。
   *
   * 没有它的话存在这样一条时序：兑换请求发出前 WS 正好在刷新旧账号的 token，
   * 兑换先落地（epoch 变了），刷新后落地并 `accept` —— 当前身份被**悄悄换回旧账号**，
   * 而界面已经说了「已找回」。加一个代次，落后的响应直接丢掉。
   */
  let epoch = 0

  const listeners = new Set<(s: AuthState) => void>()

  const snapshot = (): AuthState => ({
    account,
    deviceId: deviceId as string,
    hasCredential: persisted !== null,
    lastError,
    change: lastChange,
  })

  const emit = (change: AuthChange): void => {
    lastChange = change
    const s = snapshot()
    for (const cb of listeners) cb(s)
  }

  const persist = (r: AuthPairResponse): void => {
    persisted = { refreshToken: r.refreshToken, accountId: r.account.id, nickname: r.account.nickname }
    store.setItem(AUTH_KEY, JSON.stringify(persisted))
  }

  const clearPersisted = (): void => {
    persisted = null
    store.removeItem(AUTH_KEY)
  }

  /**
   * @param bornAt 发起这次请求时的身份代次。与当前代次不符 = 这份响应属于**上一个身份**，
   *               丢掉它（并把现在真正在用的 token 还给调用方）。
   */
  const accept = (r: AuthPairResponse, bornAt: number, via: 'register' | 'refresh' | 'adopt'): string => {
    if (bornAt !== epoch) return accessToken ?? r.accessToken
    account = r.account
    accessToken = r.accessToken
    accessExpiresAtLocal = now() + r.expiresIn
    lastError = null
    persist(r)
    emit(via)
    return r.accessToken
  }

  async function post(path: string, body: unknown): Promise<AuthPairResponse> {
    let res: Response
    try {
      res = await doFetch(`${baseUrl}/api${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      })
    } catch (e) {
      // 网络层失败不是凭据问题：绝不能因此清掉 refreshToken，否则一次地铁断网就丢账号
      throw new AuthError(`网络不可用：${String(e)}`, 'E_NETWORK', 0, false)
    }
    const text = await res.text()
    const parsed = parseJsonBody(text)
    if (!res.ok) throw readApiError(res.status, parsed)
    const pair = parsed as Partial<AuthPairResponse> | null
    if (pair == null || typeof pair.accessToken !== 'string' || typeof pair.refreshToken !== 'string') {
      throw new AuthError('服务端返回的登录响应不完整', 'E_MALFORMED', res.status, false)
    }
    return pair as AuthPairResponse
  }

  /** 返回新的 access token（而不是账号）：调用方全都紧接着要用它去连 WS。 */
  async function registerAnonymous(nickname?: string): Promise<string> {
    const bornAt = epoch
    const body: Record<string, unknown> = { deviceId }
    if (options.deviceLabel !== undefined) body['deviceLabel'] = options.deviceLabel
    if (nickname !== undefined && nickname !== '') body['nickname'] = nickname
    return accept(await post('/auth/anonymous', body), bornAt, 'register')
  }

  async function refreshOnce(): Promise<string> {
    const cred = persisted
    if (cred === null) return registerAnonymous(options.nickname?.())
    const bornAt = epoch
    try {
      const r = await post('/auth/refresh', { refreshToken: cred.refreshToken, deviceId })
      return accept(r, bornAt, 'refresh')
    } catch (e) {
      const err = e instanceof AuthError ? e : new AuthError(String(e), 'E_INTERNAL', 0, false)
      /**
       * ★ 代次对不上 = 这份**失败**属于上一个身份（邮箱登录 / 兑换邮件链接在它飞行期间落地了）。
       *
       * 成功路径早就由 `accept(r, bornAt)` 挡住了，失败路径以前没挡：
       * 旧那枚 refresh 撞上重放检测（另一个标签页刚把它轮换掉）就是 fatal，
       * 于是 `clearPersisted()` 把**刚登录进来那个账号**的 refreshToken 从
       * localStorage 里删掉，再 `registerAnonymous()` 顶一个全新的匿名号上来——
       * 面板已经说了「已登录，你的牌桌都在」，用户手里却换成了一个空账号。
       * 上一个身份的死活与新身份无关，直接丢掉这份响应。
       */
      if (bornAt !== epoch) {
        if (accessToken !== null) return accessToken
        return registerAnonymous(options.nickname?.())
      }
      lastError = err
      if (!err.fatal) {
        emit('error')
        throw err
      }
      // 凭据作废（被踢 / 重放检测 / 过期）：本地这份已经没有任何价值，
      // 留着只会在每次重连时再撞一次限流。清掉并重新匿名注册，
      // 用户会换一个新 accountId —— 这是没绑过邮箱时唯一还能继续玩的路。
      clearPersisted()
      accessToken = null
      account = null
      return registerAnonymous(options.nickname?.())
    }
  }

  const tokenUsable = (): boolean => accessToken !== null && accessExpiresAtLocal - now() > skewMs

  function run(task: () => Promise<string>): Promise<string> {
    if (inFlight !== null) return inFlight
    const p = task().finally(() => {
      inFlight = null
    })
    inFlight = p
    return p
  }

  return {
    deviceId: () => deviceId as string,
    account: () => account,
    baseUrl: () => baseUrl,
    peekAccessToken: () => accessToken,

    adopt(pair) {
      // 先进代次再 accept：这之后任何**上一个身份**的飞行中响应都会被 accept 丢掉
      epoch += 1
      accept(pair, epoch, 'adopt')
      return pair.account
    },

    updateAccount(next) {
      account = next
      emit('update')
    },

    ensureAccessToken() {
      if (tokenUsable()) return Promise.resolve(accessToken as string)
      return run(async () => {
        // 等待期间别人可能已经刷好了
        if (tokenUsable()) return accessToken as string
        return refreshOnce()
      })
    },

    forceRefresh() {
      return run(refreshOnce)
    },

    registerAnonymous(nickname) {
      return run(() => registerAnonymous(nickname)).then(() => account as PublicAccount)
    },

    async logout() {
      const cred = persisted
      // 登出也是「换人」：在途的刷新响应不该在清空之后又把旧身份装回来
      epoch += 1
      clearPersisted()
      accessToken = null
      account = null
      emit('logout')
      if (cred === null) return
      try {
        await doFetch(`${baseUrl}/api/auth/logout`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: cred.refreshToken }),
        })
      } catch {
        // 登出失败无所谓：本地凭据已经删了，服务端那条会自己过期
      }
    },

    state: snapshot,

    onChange(cb) {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
  }
}
